import { useState } from 'react'
import { PhotoProvider, PhotoView } from 'react-photo-view'
import { Box, IconButton, ImageListItemBar, Stack } from '@mui/material'
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank'
import CheckBoxIcon from '@mui/icons-material/CheckBox'
import styled from 'styled-components'

import 'react-photo-view/dist/react-photo-view.css'
import ImageNotFound from '@/assets/Image_not_available.png'

import CustomGraphy from '@/components/CustomGraphy'
import CustomView from '@/components/CustomView'

type BreakdownGalleryProps = {
  images: Array<{ id: string; url: string; description: string; subtitle: string }>
  onSelectImage: (id: string | undefined) => void
  selectedId?: string
}

const ImageGallery = styled(Box)`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
`

const ImageWrapper = styled(Box)<{ selected?: boolean }>`
  width: 100%;
  height: 186px;
  overflow: hidden;
  border-radius: 8px;
  position: relative;
  border: ${(props) => (props.selected ? '4px solid #2196F3' : '4px solid transparent')};
  transition: border 0.2s ease-in-out;
`

const ThumbImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  cursor: pointer;
  display: block;
`

function BreakdownGallery({ images, onSelectImage, selectedId }: BreakdownGalleryProps) {
  const [selectedImageId, setSelectedImageId] = useState<string | undefined>(selectedId)
  const isEmpty = images.length === 0

  const handleSelectedImage = (image?: any) => {
    if (image?.id === selectedImageId) {
      setSelectedImageId(undefined)
      onSelectImage(undefined)
    } else {
      setSelectedImageId(image?.id)
      onSelectImage(image)
    }
  }

  if (isEmpty) {
    return (
      <CustomView flexDirection="row" alignItems="center" justifyContent="space-between">
        <CustomGraphy width="100%">Sem registros</CustomGraphy>
      </CustomView>
    )
  }

  return (
    <PhotoProvider>
      <Stack sx={{ maxHeight: 400 }} spacing={2} overflow="auto">
        <ImageGallery>
          {images.map(
            (sections: any) =>
              Array.isArray(sections?.itensFoto) &&
              sections?.itensFoto?.map((item: any) => (
                <ImageWrapper key={item.id} selected={selectedImageId === item.id}>
                  <PhotoView src={item.url}>
                    <ThumbImage
                      src={item.url}
                      alt={item.description}
                      loading="lazy"
                      onError={({ currentTarget }) => {
                        if (currentTarget.src !== ImageNotFound) {
                          currentTarget.src = `${ImageNotFound}`
                        }
                      }}
                    />
                  </PhotoView>
                  <ImageListItemBar
                    sx={{
                      background:
                        'linear-gradient(to bottom, rgba(0,0,0,0.7) 0%, ' +
                        'rgba(0,0,0,0.3) 70%, rgba(0,0,0,0) 100%)',
                      '.MuiImageListItemBar-subtitle': {
                        fontSize: '1rem',
                      },
                    }}
                    title={`${sections.nomeCabecalhoItem} - ${item.description}`}
                    subtitle={item.subtitle}
                    position="top"
                    actionIcon={
                      <IconButton
                        sx={{ color: 'white' }}
                        onClick={(e) => {
                          e.stopPropagation()
                          handleSelectedImage(item)
                        }}
                      >
                        {selectedImageId == item.id ? (
                          <CheckBoxIcon fontSize="large" />
                        ) : (
                          <CheckBoxOutlineBlankIcon fontSize="large" />
                        )}
                      </IconButton>
                    }
                    actionPosition="right"
                  />
                </ImageWrapper>
              )),
          )}
        </ImageGallery>
      </Stack>
    </PhotoProvider>
  )
}

export default BreakdownGallery
